import classNames from 'classnames'
import React, { useMemo } from 'react'
import type {
  FieldValues,
  RegisterOptions,
  UseFormRegister,
} from 'react-hook-form'

import { ReactComponent as ArrowIcon } from '~/assets/icons/arrow-right.svg'
import { IFormNormalizedOption, IFormOption } from '~/types/form'
import { normalizeSelectOptions } from '~/utils/forms'

import InputErrorMessage from '../error-message'

interface Properties extends React.SelectHTMLAttributes<HTMLSelectElement> {
  name: string
  register?: UseFormRegister<FieldValues>
  registerOptions?: RegisterOptions
  options: string[] | IFormOption[]
  placeholder?: string
  variant?: 'black' | 'white'
  label?: string
  subLabel?: string
  containerClassName?: string
  error?: string
}

const Select: React.FC<Properties> = ({
  className,
  register,
  registerOptions,
  name,
  options,
  placeholder,
  variant = 'white',
  containerClassName,
  label,
  subLabel,
  id,
  error,
  ...rest
}) => {
  const normalizedOptions: IFormNormalizedOption[] = useMemo(
    () => normalizeSelectOptions(id, options),
    [id, options]
  )

  return (
    <fieldset className={classNames('w-full', containerClassName)}>
      {(label || subLabel) && (
        <legend className="w-full inline-flex items-end justify-between gap-4 ">
          {label && (
            <label
              htmlFor={id ?? `input-${name}`}
              className="text-current text-sm"
            >
              {label}
            </label>
          )}
          {subLabel && <span className="text-xs">{subLabel}</span>}
        </legend>
      )}
      <div className="relative w-full h-[3.25rem] mt-0.5">
        <select
          id={id ?? `select-${name}`}
          className={classNames(
            'h-full w-full capitalize appearance-none px-6 rounded-[1.25rem] border transition-colors focus:shadow-md focus:outline-none font-medium text-sm',
            {
              'border-gray-400 focus:border-gray-700 placeholder-gray-700 text-gray-700':
                variant === 'white',
              'bg-neutral-black text-neutral-white': variant === 'black',
            },
            className
          )}
          {...rest}
          {...(register ? register(name, registerOptions) : {})}
        >
          {placeholder && (
            <option value="" className="capitalize">
              {placeholder}
            </option>
          )}
          {normalizedOptions.map((option) => (
            <option key={option.id} value={option.value} className="capitalize">
              {option.label}
            </option>
          ))}
        </select>
        <span className="absolute z-10 right-5 top-0 h-full flex items-center pointer-events-none">
          <ArrowIcon
            className={classNames('w-2 rotate-90', {
              'text-neutral-black': variant === 'white',
              'text-neutral-white': variant === 'black',
            })}
          />
        </span>
      </div>

      {error && <InputErrorMessage message={error} />}
    </fieldset>
  )
}

export default Select
